import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import freefitLogo from "../../../img/FREEFIT_LOGO_new.png";
import WomanPilates from "../../../img/woman_sport.png";
import ScreenHeader from "../../components/ScreenHeader";
import usePassState, { PASS_STATE } from "../../hooks/usePassState";
import useUserLocation from "../../hooks/useUserLocation";
import { distanceInMeters, formatDistance } from "../../utils/geo";
import "./venue.scss";

// Path data from img/svg/carousel_location_icon.svg, inlined with currentColor so it
// follows the label beside it. The asset is a fixed #6A6A6A (darker than the label),
// and an <img>-loaded SVG cannot inherit colour at all.
const LocationGlyph = () => (
  <svg viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg" className="venue-v2-distance-icon">
    <path
      d="M0.765788 0.349214L6.03549 1.97066C6.32292 2.0591 6.35559 2.45285 6.08662 2.58733L3.85317 3.70403C3.78866 3.73629 3.73633 3.78861 3.70407 3.85312L2.58741 6.08644C2.45292 6.35541 2.05925 6.3228 1.97081 6.03538L0.349209 0.765684C0.270514 0.509926 0.510029 0.270519 0.765788 0.349214Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.666667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function Venue({ data, price, btnText, onCtaClick, onOpenMap }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [aboutExpanded, setAboutExpanded] = useState(false);
  // Above the early return below, so hook order stays stable while `data` loads.
  const { coords } = useUserLocation();
  const passState = usePassState();
  // Usable = has entries left (NumberOfEntries counts used entries) — the same
  // rule the check-in flow applies in screens/venue.jsx to redeem from the pass.
  const includedInPass = passState.state === PASS_STATE.ACTIVE && (passState.pass?.NumberOfEntries ?? 4) < 4;

  const header = <ScreenHeader title={t("ACTIVITY_ORDER")} onBack={() => navigate(-1)} />;

  if (!data || Object.keys(data).length === 0) {
    return <div className="venue-v2">{header}</div>;
  }

  const heroSrc = data.Images && data.Images.length > 0 && data.Images[0].Path ? data.Images[0].Path : WomanPilates;
  // V2 CTA wording: check-in venues → "צ'ק אין", phone-coordination venues → "לתיאום"; calendar keeps its label.
  const ctaLabel =
    btnText === t("VENUE_ENTER") ? t("VENUE_CHECK_IN") : btnText === t("VENUE_BOOK") ? t("VENUE_COORDINATE") : btnText;
  const openingHours = [data.OpeningHoursSundayToThursday, data.OpeningHoursFriday, data.OpeningHoursSaturday].filter(Boolean);
  // GetDetailedClubInfoByID spells the club coordinates lowercase. Null — no fix,
  // denied prompt, insecure context, club without coordinates — drops the line
  // entirely rather than rendering a gap, as the home cards and club list do.
  const distanceLabel = formatDistance(
    distanceInMeters(coords, { latitude: data.latitude, longitude: data.longitude }),
    t,
  );

  return (
    <div className="venue-v2">
      {header}
      <div className="venue-v2-hero">
        <img src={heroSrc} alt={data.Name} />
      </div>

      <div className="venue-v2-content">
        <div className="venue-v2-title-row">
          <p className="venue-v2-name">{data.Name}</p>
          <img className="venue-v2-logo" src={freefitLogo} alt="freefit" />
        </div>

        {/* Address and distance are one tap target for the map. */}
        <button type="button" className="venue-v2-location" onClick={onOpenMap} aria-label={t("NAVIGATION")}>
          <span className="venue-v2-address">{data.Address}</span>
          {distanceLabel && (
            <span className="venue-v2-distance">
              <LocationGlyph />
              {distanceLabel}
            </span>
          )}
        </button>

        {data.TextAbout && (
          <div className="venue-v2-section">
            <p className="venue-v2-section-title">{t("VENUE_ABOUT_US")}</p>
            <p className={`venue-v2-about${aboutExpanded ? "" : " venue-v2-about--clamped"}`}>{data.TextAbout}</p>
            <button type="button" className="venue-v2-about-toggle" onClick={() => setAboutExpanded(!aboutExpanded)}>
              {aboutExpanded ? t("SHOW_LESS") : t("SHOW_MORE")}
            </button>
          </div>
        )}

        {openingHours.length > 0 && (
          <div className="venue-v2-section">
            <p className="venue-v2-section-title">{t("VENUE_HOURS")}</p>
            {openingHours.map((line, i) => (
              <p className="venue-v2-hours-row" key={i}>
                {line}
              </p>
            ))}
          </div>
        )}

        {data.Phone && (
          <div className="venue-v2-section">
            <p className="venue-v2-section-title">{t("VENUE_CONTACT")}</p>
            <a className="venue-v2-contact-link" href={`tel:${data.Phone.split("-").join("")}`}>
              <span className="venue-v2-contact-label">{t("VENUE_CONTACT_BY_PHONE")}</span>
              {` - ${data.Phone}`}
            </a>
          </div>
        )}
      </div>

      <div className="venue-v2-bottom-bar">
        {includedInPass ? (
          <p className="venue-v2-price venue-v2-included">{t("VENUE_INCLUDED_IN_PASS")}</p>
        ) : price > 0 && passState.state !== PASS_STATE.HIDDEN ? (
          // "hidden" = passes not fetched yet; keep the price off so a pass
          // holder never sees ₪ flash before the label swaps in.
          <p className="venue-v2-price">₪{price}</p>
        ) : (
          <span />
        )}
        <button type="button" className="venue-v2-cta" onClick={onCtaClick}>
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

export default Venue;
